/**
 * Households by number (1.5.2): the small card shown wherever the apartment is shown ("Household of 3 — Sharon,
 * James, James Jr.", or "Household of 3" when the names are not known) and the editor on People and the Companion
 * sign-up (size 1–4, up to three names, any of them blank — "we know how many, not always who").
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HOUSEHOLD_MAX_SIZE, householdNames, type HouseholdView } from '@shared/household';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';

export function useHousehold(tenantId: string | null | undefined, eventId?: string | null) {
  return useQuery({
    queryKey: ['household', tenantId ?? '', eventId ?? ''],
    queryFn: () => apiFetch<HouseholdView>(`/api/households/${encodeURIComponent(tenantId!)}${eventId ? `?event=${encodeURIComponent(eventId)}` : ''}`),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/** "Household of 3 — Sharon, James, James Jr." (or "Household of 3"). */
export function householdLine(h: Pick<HouseholdView, 'size' | 'head' | 'members'>, t: (k: string, o?: Record<string, unknown>) => string): string {
  const names = householdNames(h);
  return names.length > 1 ? t('household.ofNamed', { count: h.size, names: names.join(', ') }) : t('household.of', { count: h.size });
}

export function HouseholdCard({ tenantId, eventId, testId = 'household-card' }: { tenantId: string | null | undefined; eventId?: string | null; testId?: string }) {
  const { t } = useTranslation();
  const h = useHousehold(tenantId, eventId);
  if (!tenantId || !h.data) return null;
  return (
    <p className="trans household-card" data-testid={testId}>
      <Icon name="people" aria-hidden="true" /> <span>{householdLine(h.data, t)}</span>
    </p>
  );
}

export function HouseholdEditor({ tenantId, idPrefix = 'hh', fix = false }: { tenantId: string; idPrefix?: string; /** 1.5.4: People after Import — Swap head and member, Not a household. */ fix?: boolean }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const qc = useQueryClient();
  const h = useHousehold(tenantId);
  const [size, setSize] = useState(1);
  const [names, setNames] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    if (!h.data) return;
    setSize(h.data.size);
    setNames(h.data.members);
  }, [h.data]);
  const others = Array.from({ length: Math.max(0, size - 1) }, (_, i) => names[i] ?? '');
  const dirty = !!h.data && (size !== h.data.size || others.join('|') !== h.data.members.slice(0, size - 1).join('|'));
  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      const v = await apiFetch<HouseholdView>(`/api/people/${encodeURIComponent(tenantId)}/household`, { method: 'PUT', body: JSON.stringify({ size, members: others.map((x) => x.trim()) }) });
      qc.setQueryData(['household', tenantId, ''], v);
      void qc.invalidateQueries({ queryKey: ['household', tenantId] });
      const m = t('household.saved', { line: householdLine(v, t) });
      setNote({ ok: true, text: m });
      announce(m);
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? t('household.failed')) : t('household.failed');
      setNote({ ok: false, text: m });
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  // 1.5.4: the same corrections the import's dry run offers, on the card after Import
  const fixIt = async (body: { action: 'swap'; member: number } | { action: 'none' }, done: (v: HouseholdView) => string) => {
    setBusy(true);
    setNote(null);
    try {
      const v = await apiFetch<HouseholdView>(`/api/people/${encodeURIComponent(tenantId)}/household/fix`, { method: 'POST', body: JSON.stringify(body) });
      qc.setQueryData(['household', tenantId, ''], v);
      void qc.invalidateQueries({ queryKey: ['household', tenantId] });
      void qc.invalidateQueries({ queryKey: ['person', tenantId] });
      void qc.invalidateQueries({ queryKey: ['people'] });
      const m = done(v);
      setNote({ ok: true, text: m });
      announce(m);
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? t('household.failed')) : t('household.failed');
      setNote({ ok: false, text: m });
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  const named = (h.data?.members ?? []).map((m, i) => ({ m, i })).filter((x) => x.m.trim());
  return (
    <fieldset className="field household-editor" data-testid="household-editor">
      <legend className="eyebrow">{t('household.title')}</legend>
      <p className="help" id={`${idPrefix}-help`}>
        {t('household.help')}
      </p>
      <div className="field">
        <label htmlFor={`${idPrefix}-size`}>{t('household.size')}</label>
        <select id={`${idPrefix}-size`} className="input" style={{ width: 'auto' }} value={size} disabled={!h.data} onChange={(e) => setSize(Number(e.target.value))} aria-describedby={`${idPrefix}-help`} data-testid="household-size">
          {Array.from({ length: HOUSEHOLD_MAX_SIZE }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {t('household.sizeOption', { count: n })}
            </option>
          ))}
        </select>
      </div>
      {others.map((v, i) => (
        <div className="field" key={i}>
          <label htmlFor={`${idPrefix}-m${i}`}>{t('household.memberLabel', { n: i + 2 })}</label>
          <input
            id={`${idPrefix}-m${i}`}
            className="input"
            value={v}
            maxLength={60}
            autoComplete="off"
            onChange={(e) => setNames(others.map((x, j) => (j === i ? e.target.value : x)))}
            data-testid={`household-member-${i + 2}`}
          />
        </div>
      ))}
      <div className="chips">
        <Button size="sm" disabled={busy || !dirty} onClick={() => save()} data-testid="household-save">
          {busy ? t('household.saving') : t('household.save')}
        </Button>
        {note ? (
          <span className={note.ok ? 'help' : 'field-err'} role={note.ok ? undefined : 'alert'} data-testid="household-note">
            {note.text}
          </span>
        ) : null}
      </div>
      {fix && h.data && h.data.size > 1 ? (
        <div className="chips" style={{ marginTop: 8 }} role="group" aria-label={t('household.fixLabel')} data-testid="household-fix">
          {named.map(({ m, i }) => (
            <Button key={i} size="sm" variant="secondary" disabled={busy} onClick={() => fixIt({ action: 'swap', member: i }, (v) => t('household.swapped', { head: v.head }))} data-testid={`household-swap-${i}`}>
              {t('household.swap', { name: m })}
            </Button>
          ))}
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => fixIt({ action: 'none' }, () => t('household.notHouseholdDone'))} data-testid="household-not">
            {t('household.notHousehold')}
          </Button>
        </div>
      ) : null}
    </fieldset>
  );
}

/**
 * 22 Oct 2026 (D-1022-TENANT2): the household questions without their own Save — the Edit sheet holds the value and
 * saves it with everything else (PUT /api/people/<id>/household, only when it changed).
 */
export interface HouseholdValue {
  size: number;
  names: string[];
}
export const householdValueOf = (h: Pick<HouseholdView, 'size' | 'members'>): HouseholdValue => ({ size: h.size, names: h.members.slice(0, Math.max(0, h.size - 1)) });
export const householdOthers = (v: HouseholdValue): string[] => Array.from({ length: Math.max(0, v.size - 1) }, (_, i) => (v.names[i] ?? '').trim());
export const householdChanged = (v: HouseholdValue, h: Pick<HouseholdView, 'size' | 'members'>): boolean => v.size !== h.size || householdOthers(v).join('|') !== h.members.slice(0, Math.max(0, h.size - 1)).map((x) => x.trim()).join('|');

export function HouseholdFields({ value, onChange, idPrefix = 'ed-hh' }: { value: HouseholdValue; onChange: (v: HouseholdValue) => void; idPrefix?: string }) {
  const { t } = useTranslation();
  const others = Array.from({ length: Math.max(0, value.size - 1) }, (_, i) => value.names[i] ?? '');
  return (
    <fieldset className="field household-editor" data-testid="household-editor">
      <legend className="eyebrow">{t('household.title')}</legend>
      <p className="help" id={`${idPrefix}-help`}>
        {t('household.help')}
      </p>
      <div className="field">
        <label htmlFor={`${idPrefix}-size`}>{t('household.size')}</label>
        <select id={`${idPrefix}-size`} className="input" style={{ width: 'auto' }} value={value.size} onChange={(e) => onChange({ ...value, size: Number(e.target.value) })} aria-describedby={`${idPrefix}-help`} data-testid="household-size">
          {Array.from({ length: HOUSEHOLD_MAX_SIZE }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {t('household.sizeOption', { count: n })}
            </option>
          ))}
        </select>
      </div>
      {others.map((v, i) => (
        <div className="field" key={i}>
          <label htmlFor={`${idPrefix}-m${i}`}>{t('household.memberLabel', { n: i + 2 })}</label>
          <input id={`${idPrefix}-m${i}`} className="input" value={v} maxLength={60} autoComplete="off" onChange={(e) => onChange({ ...value, names: others.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`household-member-${i + 2}`} />
        </div>
      ))}
    </fieldset>
  );
}

/** The after-import corrections (Swap head and member, Not a household): their own presses, on the page's Contact & household section. */
export function HouseholdFixes({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const qc = useQueryClient();
  const h = useHousehold(tenantId);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  // after "Not a household" the household is one person: the line stays until its answer has been read
  if (!h.data || (h.data.size < 2 && !note)) return null;
  const fixIt = async (body: { action: 'swap'; member: number } | { action: 'none' }, done: (v: HouseholdView) => string) => {
    setBusy(true);
    setNote(null);
    try {
      const v = await apiFetch<HouseholdView>(`/api/people/${encodeURIComponent(tenantId)}/household/fix`, { method: 'POST', body: JSON.stringify(body) });
      qc.setQueryData(['household', tenantId, ''], v);
      void qc.invalidateQueries({ queryKey: ['household', tenantId] });
      void qc.invalidateQueries({ queryKey: ['person', tenantId] });
      void qc.invalidateQueries({ queryKey: ['people'] });
      const m = done(v);
      setNote({ ok: true, text: m });
      announce(m);
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? t('household.failed')) : t('household.failed');
      setNote({ ok: false, text: m });
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  const named = h.data.members.map((m, i) => ({ m, i })).filter((x) => x.m.trim());
  return (
    <div className="chips" role="group" aria-label={t('household.fixLabel')} data-testid="household-fix">
      {named.map(({ m, i }) => (
        <Button key={i} size="sm" variant="secondary" disabled={busy} onClick={() => fixIt({ action: 'swap', member: i }, (v) => t('household.swapped', { head: v.head }))} data-testid={`household-swap-${i}`}>
          {t('household.swap', { name: m })}
        </Button>
      ))}
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => fixIt({ action: 'none' }, () => t('household.notHouseholdDone'))} data-testid="household-not">
        {t('household.notHousehold')}
      </Button>
      {note ? (
        <span className={note.ok ? 'help' : 'field-err'} role={note.ok ? undefined : 'alert'} data-testid="household-note">
          {note.text}
        </span>
      ) : null}
    </div>
  );
}

/** Saves the household (PUT) — the Edit sheet calls it only when the value changed. */
export async function saveHousehold(tenantId: string, v: HouseholdValue): Promise<HouseholdView> {
  return apiFetch<HouseholdView>(`/api/people/${encodeURIComponent(tenantId)}/household`, { method: 'PUT', body: JSON.stringify({ size: v.size, members: householdOthers(v) }) });
}
